import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { exitDocumentFullscreen, fullscreenPortalHost } from 'components/answers/videoFullscreen'
import cn from 'classnames'
import { ArrowBigUp, Bitcoin } from 'lucide-react'
import { formatUsdValue, usdToSats } from 'api/useBsvExchangeRate'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { resolveUpvalueOwnerId } from 'components/panels/ordinalUpvalueFeature'
import {
    BITCOIN_UNIT,
    DEFAULT_BOOST_SATS,
    DEFAULT_UPVALUE_SATS,
    SATOSHI_UNIT,
    USD_UNIT,
    toBitcoin,
} from 'lib/bsv-util'
import { sameUserId } from 'state/freeReactionEligibility'
import useStore from 'state/knovStore'
import {
    canSendFreeReactionForAnswer,
    nextPressIsFreeReaction,
    reactOrUpvalue,
    upvaluesEnabledForAnswer,
    type ReactOrUpvalueOptions,
} from 'state/upvalueQueue'
import type { Answer } from 'types/answers'
import SatsIcon from 'components/shared/SatsIcon'
import UpvalueGlyph from '../UpvalueGlyph'
import { DEFAULT_UPVALUE_EMOJI } from '../upvalueEmoji'
import { LARGE_UPVALUE_CONFIRM_MODAL_KEY } from '../upvalueModalKeys'
import { planQuickUpvalue } from '../quickUpvalue'
import { UpvalueEffect } from './UpvalueEffect'
import styles from './quick-upvalue-button.module.scss'

export type QuickUpvalueTarget = {
    answer: Answer
    artifactOrdinalId?: string | null
    artifactOwnerId?: string | null
}

type QuickUpvalueButtonProps = {
    // Looked up on each tap, so the button follows whatever is playing.
    resolveTarget: () => QuickUpvalueTarget | null | Promise<QuickUpvalueTarget | null>
    // What resolveTarget points at right now (song id, now-playing media key).
    // Taps share an in-flight lookup only while it's the same target, so a
    // track change mid-fetch never pays the previous track.
    targetKey?: string | null
    source: string
    authSource?: string
    reactionEmoji?: string
    sentimentSource?: string
    // 'pill' is the glyph + default amount; 'arrow' is a bare arrow styled by className.
    variant?: 'pill' | 'arrow'
    className?: string
    iconSize?: number
    iconStrokeWidth?: number
    tabIndex?: number
    ariaLabel?: string
    testId?: string
    onPress?: () => void
}

// error: why a tap sent nothing, shown in place of the total.
type Burst = {
    id: number
    sats: number
    error: string | null
    x: number
    y: number
    below: boolean
}

const BURST_IDLE_MS = 1500
// Buttons this close to the top edge (the fullscreen top rail) float it below.
const BURST_ROOM_ABOVE_PX = 40

/**
 * The one-tap upvalue shared by the Music header and the media dock. Taps can
 * be spammed like the answer's quick signal button: each one queues the
 * default amount, fires the upvalue effect, and grows a floating "+amount"
 * drawn above the effect until the taps stop.
 */
export default function QuickUpvalueButton(props: QuickUpvalueButtonProps) {
    const effectEnabled = useStore(state => state.effectEnabled)
    return (
        <UpvalueEffect effectEnabled={effectEnabled}>
            <QuickUpvalueButtonInner {...props} />
        </UpvalueEffect>
    )
}

function QuickUpvalueButtonInner({
    resolveTarget,
    targetKey,
    source,
    authSource,
    reactionEmoji,
    sentimentSource,
    variant = 'pill',
    className,
    iconSize = 20,
    iconStrokeWidth = 2,
    tabIndex,
    ariaLabel = 'Upvalue this post',
    testId,
    onPress,
    onEffectTrigger,
    clickPower = 0,
}: QuickUpvalueButtonProps & {
    onEffectTrigger?: (element: HTMLElement | null) => void
    clickPower?: number
}) {
    const configuredUpvalueSats = useStore(state => state.upvalueSats) || DEFAULT_UPVALUE_SATS
    const configuredUpvalueUsd = useStore(state => state.upvalueUsd)
    const boostSats = useStore(state => state.boostSats) || DEFAULT_BOOST_SATS
    const signalType = useStore(state => state.signalType)
    const { bsvUnit, exchangeRate, formatSatsAmount, displayUnit } = useBsvDisplay()
    const numericUpvalueUsd = Number(configuredUpvalueUsd)
    const useConfiguredUsd =
        bsvUnit === USD_UNIT && Number.isFinite(numericUpvalueUsd) && numericUpvalueUsd > 0
    const upvalueSats =
        (useConfiguredUsd && exchangeRate && usdToSats(numericUpvalueUsd, exchangeRate)) ||
        configuredUpvalueSats
    const upvalueUsdLabel = useConfiguredUsd ? formatUsdValue(numericUpvalueUsd) : null
    const amountLabel = upvalueUsdLabel || formatSatsAmount(upvalueSats, { includeUnit: false })
    const titleLabel = upvalueUsdLabel || formatSatsAmount(upvalueSats)
    const amountUnit = displayUnit(upvalueSats)

    const buttonRef = useRef<HTMLButtonElement>(null)
    const resolvingRef = useRef<{
        key: string | null | undefined
        promise: Promise<QuickUpvalueTarget | null> | null
    }>({ key: null, promise: null })
    const burstTimerRef = useRef<number>()
    const [burst, setBurst] = useState<Burst | null>(null)
    useEffect(() => () => window.clearTimeout(burstTimerRef.current), [])

    const flash = useCallback((sats: number, error: string | null = null) => {
        const rect = buttonRef.current?.getBoundingClientRect()
        const x = rect ? rect.left + rect.width / 2 : 0
        const below = rect ? rect.top < BURST_ROOM_ABOVE_PX : false
        const y = rect ? (below ? rect.bottom : rect.top) : 0
        setBurst(prev => ({
            id: (prev?.id || 0) + 1,
            sats: error ? 0 : (prev && !prev.error ? prev.sats : 0) + sats,
            error,
            x,
            y,
            below,
        }))
        window.clearTimeout(burstTimerRef.current)
        burstTimerRef.current = window.setTimeout(() => setBurst(null), BURST_IDLE_MS)
    }, [])

    const handleClick = async (event: React.MouseEvent) => {
        event.stopPropagation()
        onPress?.()
        const currentUser = gon?.currentUser as { id: string | number } | undefined
        if (!currentUser) {
            useStore.getState().actions.modalActions.openAuthModal('signup', authSource || source)
            return
        }

        let target: QuickUpvalueTarget | null = null
        try {
            if (!resolvingRef.current.promise || resolvingRef.current.key !== targetKey) {
                resolvingRef.current = {
                    key: targetKey,
                    promise: Promise.resolve().then(resolveTarget),
                }
            }
            const pending = resolvingRef.current
            try {
                target = await pending.promise
            } finally {
                if (resolvingRef.current === pending) {
                    resolvingRef.current = { key: null, promise: null }
                }
            }
        } catch {
            target = null
        }
        const answer = target?.answer
        if (!answer?.id) {
            flash(0, 'Failed')
            return
        }

        const ownerId = resolveUpvalueOwnerId(
            answer.user_id,
            target?.artifactOwnerId,
            answer.artifact_owner_id,
        )
        const plan = planQuickUpvalue({
            signalType,
            ownsTarget: sameUserId(currentUser.id, ownerId),
            nextPressIsFree: nextPressIsFreeReaction(answer),
            upvaluesEnabled: upvaluesEnabledForAnswer(answer),
            freeReactionAvailable: canSendFreeReactionForAnswer(answer),
            upvalueSats,
            boostSats,
        })
        if (plan.kind === 'blocked') return

        const options: ReactOrUpvalueOptions = {
            source,
            artifactOrdinalId: target?.artifactOrdinalId || undefined,
            reactionEmoji,
            sentimentSource,
        }
        if (plan.kind === 'free') {
            reactOrUpvalue(answer, upvalueSats, 'upvalue', options)
            flash(0)
            return
        }

        // Feedback rides with the send, so a large amount only counts once confirmed.
        const send = () => {
            onEffectTrigger?.(buttonRef.current)
            flash(plan.sats)
            reactOrUpvalue(answer, plan.sats, plan.type, options)
        }
        if (!plan.confirm) {
            send()
            return
        }
        // The confirm renders on the body, which a fullscreen dock would hide.
        exitDocumentFullscreen()
        useStore
            .getState()
            .actions.modalActions.openConfirmModal(
                `Upvalue ${toBitcoin(plan.sats)} bitcoin?`,
                'Upvalue',
                send,
                {
                    headerTitle: 'Upvalue',
                    modalKey: LARGE_UPVALUE_CONFIRM_MODAL_KEY,
                    iconType: 'upvalue',
                    confirmVariant: 'bsv',
                    disableOverlayBlur: true,
                },
            )
    }

    const burstLabel = burst
        ? burst.error
            ? burst.error
            : burst.sats > 0
            ? `+${formatSatsAmount(burst.sats)}`
            : '+1'
        : null

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={cn(
                    variant === 'pill' && styles.pill,
                    className,
                    burst && !burst.error && styles.pulse,
                )}
                onClick={handleClick}
                aria-label={ariaLabel}
                title={`Upvalue (+${titleLabel})`}
                tabIndex={tabIndex}
                data-testid={testId}
            >
                {variant === 'pill' ? (
                    <>
                        <UpvalueGlyph emoji={DEFAULT_UPVALUE_EMOJI} size={14} />
                        {upvalueUsdLabel ? null : amountUnit === BITCOIN_UNIT ? (
                            <span className={styles.pillUnit}>
                                <Bitcoin aria-hidden="true" />
                            </span>
                        ) : amountUnit === SATOSHI_UNIT ? (
                            <SatsIcon inline contextStyles={styles.pillUnit} />
                        ) : null}
                        <span className={styles.pillAmount}>{amountLabel}</span>
                    </>
                ) : (
                    <ArrowBigUp size={iconSize} strokeWidth={iconStrokeWidth} aria-hidden="true" />
                )}
            </button>
            {burst &&
                typeof document !== 'undefined' &&
                ReactDOM.createPortal(
                    // Portaled above the effect's canvas so the running total
                    // stays readable while the bubbles play.
                    <span
                        key={burst.id}
                        className={cn(
                            styles.burst,
                            burst.below && styles.burstBelow,
                            burst.error && styles.burstMuted,
                        )}
                        style={
                            {
                                left: `${burst.x}px`,
                                top: `${burst.y}px`,
                                '--burst-scale': 1 + Math.min(clickPower, 20) * 0.04,
                            } as React.CSSProperties
                        }
                        aria-live="polite"
                    >
                        {burstLabel}
                    </span>,
                    fullscreenPortalHost(),
                )}
        </>
    )
}
